/**
 * WCAG 2.1 contrast ratio helpers for the ColorPicker + BrandingEditor.
 *
 * AA threshold for normal text is 4.5:1. Branding forms warn (but do not
 * block save) when the primary colour has less than AA contrast against
 * white/black, because some brand palettes accept a soft violation.
 */

export interface Rgb {
  r: number; // 0..255
  g: number;
  b: number;
}

export function hexToRgb(hex: string): Rgb | null {
  const clean = hex.replace("#", "").trim();
  if (clean.length === 3) {
    return {
      r: parseInt(clean[0]! + clean[0]!, 16),
      g: parseInt(clean[1]! + clean[1]!, 16),
      b: parseInt(clean[2]! + clean[2]!, 16),
    };
  }
  if (clean.length === 6) {
    return {
      r: parseInt(clean.slice(0, 2), 16),
      g: parseInt(clean.slice(2, 4), 16),
      b: parseInt(clean.slice(4, 6), 16),
    };
  }
  return null;
}

function channelLuminance(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(rgb: Rgb): number {
  return (
    0.2126 * channelLuminance(rgb.r) +
    0.7152 * channelLuminance(rgb.g) +
    0.0722 * channelLuminance(rgb.b)
  );
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [lighter, darker] = la >= lb ? [la, lb] : [lb, la];
  return (lighter + 0.05) / (darker + 0.05);
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

export interface ContrastCheck {
  againstWhite: number;
  againstBlack: number;
  bestForeground: "white" | "black";
  passesAA: boolean;    // 4.5:1 against the best foreground
  passesAAA: boolean;   // 7:1 against the best foreground
}

export function checkContrast(hex: string): ContrastCheck | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const againstWhite = contrastRatio(rgb, WHITE);
  const againstBlack = contrastRatio(rgb, BLACK);
  const best = Math.max(againstWhite, againstBlack);
  return {
    againstWhite,
    againstBlack,
    bestForeground: againstWhite >= againstBlack ? "white" : "black",
    passesAA: best >= 4.5,
    passesAAA: best >= 7,
  };
}

/** Normalise "#abc", "abc", "#aabbcc", "aabbcc" → "#aabbcc". Returns null if invalid. */
export function normaliseHex(input: string): string | null {
  const rgb = hexToRgb(input);
  if (!rgb) return null;
  const hex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${hex(rgb.r)}${hex(rgb.g)}${hex(rgb.b)}`.toLowerCase();
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tenant legibility — the three tiers (MV2-837, ds/WhiteLabelAction)
 *
 * A tenant picks their brand hex; we cannot design it. What the system
 * guarantees instead is that it is LEGIBLE as a filled control, and it does
 * that without ever altering their hue:
 *
 *   1. Pick the ink   — whichever of near-black or white clears 4.5:1 on the
 *                       fill (WCAG 1.4.3, text on the button).
 *   2. Give it an edge — a fill within 3:1 of the page has no visible boundary
 *                       (WCAG 1.4.11), so add a 1px border in the same hue,
 *                       moved away from the page.
 *   3. Move lightness  — last resort, only when an edge cannot save it: the
 *                       fill sits on the page's own side of the scale (a dark
 *                       fill on a dark ground), or the ink fails outright.
 *                       Hue and saturation are held.
 *
 * Measured the right way round: ink ON the fill at 4.5:1, fill AGAINST the
 * page at 3:1. Measuring the fill against the page at 4.5 — an earlier pass —
 * wrongly condemns nine of the fleet's ten brand colours.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Ink candidates. Near-black rather than pure black — it matches --gray-900's
 *  warmth on a saturated fill and is what `--brand-primary-fg` already uses. */
const INK_DARK = "#0b1a11";
const INK_LIGHT = "#ffffff";

/** WCAG 1.4.3 — text on the fill. */
const INK_FLOOR = 4.5;
/** WCAG 1.4.11 — the control's own boundary against the page. */
const EDGE_FLOOR = 3;

export interface Hsl {
  h: number; // 0..360
  s: number; // 0..1
  l: number; // 0..1
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  h *= 60;
  return { h: h < 0 ? h + 360 : h, s, l };
}

export function hslToHex({ h, s, l }: Hsl): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const [r1, g1, b1] =
    hp < 1 ? [c, x, 0]
    : hp < 2 ? [x, c, 0]
    : hp < 3 ? [0, c, x]
    : hp < 4 ? [0, x, c]
    : hp < 5 ? [x, 0, c]
    : [c, 0, x];
  const m = l - c / 2;
  const to = (v: number) =>
    Math.round(Math.min(1, Math.max(0, v + m)) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${to(r1)}${to(g1)}${to(b1)}`;
}

/** Contrast between two hexes. Returns 1 (the "identical" floor) if either
 *  fails to parse, so a malformed value degrades to "needs help", never to a
 *  false pass. */
export function contrastHex(a: string, b: string): number {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  if (!ra || !rb) return 1;
  return contrastRatio(ra, rb);
}

/** The ink that reads best on `fill`, with its ratio. */
function bestInk(fill: string): { ink: string; ratio: number } {
  const onDark = contrastHex(fill, INK_DARK);
  const onLight = contrastHex(fill, INK_LIGHT);
  return onDark >= onLight ? { ink: INK_DARK, ratio: onDark } : { ink: INK_LIGHT, ratio: onLight };
}

export interface ActionColour {
  /** The button background — the tenant's hex unless tier 3 fired. */
  fill: string;
  /** Text/icon colour on that fill. */
  ink: string;
  /** 1px border, or "transparent" when the fill already has a visible edge. */
  edge: string;
  /** Which tier the colour needed: 1 fine as-is, 2 needed an edge, 3 moved. */
  tier: 1 | 2 | 3;
  /** Ink-on-fill ratio, after any tier-3 move. Floor 4.5. */
  inkRatio: number;
  /** Fill-against-page ratio, after any tier-3 move. Floor 3. */
  groundRatio: number;
  /** True when tier 3 had to move lightness — the hue is still theirs. */
  adjusted: boolean;
  /** Both floors met. This is the gate behind `wcag_contrast_validated`. */
  valid: boolean;
}

/**
 * Resolve a tenant's brand hex into a legible filled control on `ground`.
 *
 * `ground` is the page colour for the theme being rendered — `--gray-0`, i.e.
 * `#ffffff` on light and `#141720` on dark. The SAME brand hex goes in for
 * both themes: the light/dark column swap is dropped for tenants (decision
 * 4 Sep), so Copper Coast stays orange in dark instead of turning teal.
 *
 * Returns null only for an unparseable hex — the caller then leaves the
 * platform default in place rather than emitting a broken var.
 */
export function resolveActionColour(brandHex: string, ground: string): ActionColour | null {
  const start = normaliseHex(brandHex);
  const groundHex = normaliseHex(ground);
  if (!start || !groundHex) return null;

  const groundRgb = hexToRgb(groundHex)!;
  const groundLum = relativeLuminance(groundRgb);
  // Which way is "away from the page"? On white you go darker; on the dark
  // ground you go lighter.
  const awayIsDarker = groundLum > 0.5;
  const startHsl = rgbToHsl(hexToRgb(start)!);

  let fill = start;
  let ink = bestInk(start);
  let groundRatio = contrastHex(start, groundHex);
  let adjusted = false;

  // Can an edge rescue this fill? On the white page, always: there are twenty
  // stops of headroom below any fill, so a darker border is always available.
  // On the dark page there is almost nothing below --gray-0, so a fill that is
  // itself within 2:1 of the ground already reads AS the ground, and ringing it
  // in something lighter draws an outline rather than a button — the fill has
  // to move. That asymmetry is what separates ACEL's lime on white (1.76, gets
  // a border) from Entegral's forest green on dark (1.90, gets lightened).
  const EDGE_VIABLE_ON_DARK = 2;
  const edgeCanRescue = awayIsDarker || groundRatio >= EDGE_VIABLE_ON_DARK;

  const needsMove = ink.ratio < INK_FLOOR || (groundRatio < EDGE_FLOOR && !edgeCanRescue);

  if (needsMove) {
    // Walk lightness away from the page in 1% steps, holding hue and
    // saturation, until both floors clear. 100 steps covers the full ramp; if
    // nothing clears (a fully desaturated hex on its own ground) we keep the
    // best candidate seen rather than returning something worse than we
    // started with.
    let best = { fill, inkRatio: ink.ratio, groundRatio, score: -Infinity };
    for (let step = 1; step <= 100; step++) {
      const l = awayIsDarker ? startHsl.l - step / 100 : startHsl.l + step / 100;
      if (l < 0 || l > 1) break;
      const candidate = hslToHex({ ...startHsl, l });
      const candInk = bestInk(candidate);
      const candGround = contrastHex(candidate, groundHex);
      if (candInk.ratio >= INK_FLOOR && candGround >= EDGE_FLOOR) {
        best = { fill: candidate, inkRatio: candInk.ratio, groundRatio: candGround, score: Infinity };
        break;
      }
      // Not there yet — rank by how much of each shortfall is left.
      const score = Math.min(candInk.ratio / INK_FLOOR, 1) + Math.min(candGround / EDGE_FLOOR, 1);
      if (score > best.score) {
        best = { fill: candidate, inkRatio: candInk.ratio, groundRatio: candGround, score };
      }
    }
    fill = best.fill;
    ink = bestInk(fill);
    groundRatio = best.groundRatio;
    adjusted = fill !== start;
  }

  // Tier 2: still short of a visible edge → same hue, one step further from
  // the page. 18 lightness points is the smallest move that clears 3:1 for
  // every colour in the fleet while still reading as the same colour.
  const needsEdge = groundRatio < EDGE_FLOOR;
  const fillHsl = rgbToHsl(hexToRgb(fill)!);
  const edge = needsEdge
    ? hslToHex({
        ...fillHsl,
        l: Math.min(1, Math.max(0, awayIsDarker ? fillHsl.l - 0.18 : fillHsl.l + 0.18)),
      })
    : "transparent";

  const tier: 1 | 2 | 3 = adjusted ? 3 : needsEdge ? 2 : 1;

  return {
    fill,
    ink: ink.ink,
    edge,
    tier,
    inkRatio: ink.ratio,
    groundRatio,
    adjusted,
    // Tier 2 is a pass: the edge IS how the 1.4.11 floor is met.
    valid: ink.ratio >= INK_FLOOR && (groundRatio >= EDGE_FLOOR || needsEdge),
  };
}

/** The page colour per theme — `--gray-0` in tokens.css. */
export const GROUND_LIGHT = "#ffffff";
export const GROUND_DARK = "#141720";
